import BorderIcon from '@nerdwallet/react-icon/material/BorderAll';
import ComponentIcon from '@nerdwallet/react-icon/material/Build';
import LayoutIcon from '@nerdwallet/react-icon/material/Dashboard';
import ContainerIcon from '@nerdwallet/react-icon/material/GridOn';
import InfoIcon from '@nerdwallet/react-icon/material/Info';
import DimensionsIcon from '@nerdwallet/react-icon/material/OpenWith';
import StyleIcon from '@nerdwallet/react-icon/material/Palette';
// import SettingsIcon from '@nerdwallet/react-icon/material/Settings';
import AnalyticsIcon from '@nerdwallet/react-icon/material/InsertChart';
import FloatingIcon from '@nerdwallet/react-icon/material/PictureInPicture';
import SpacingIcon from '@nerdwallet/react-icon/material/ViewColumn';
import HideIcon from '@nerdwallet/react-icon/material/Visibility';

import {
  boolCheckboxSpec,
  collapsable,
  colorPicker,
  group,
  numberInputSpec,
  repeatable,
  stringInputSpec,
  stringSelectSpec,
  stringTextAreaSpec,
} from '~/app/lib/editing-configs';

const heightAndWidthUnitOptions = [
  { label: 'Pixels', value: 'px' },
  { label: 'Percent', value: '%' },
  { label: 'Rem', value: 'rem' },
  { label: 'Auto', value: 'auto' },
];

export const marginAndPaddingUnitOptions = [
  { label: 'Pixels', value: 'px' },
  { label: 'Percent', value: '%' },
  { label: 'Rem', value: 'rem' },
];

export const floatingUnitOptions = [
  { label: 'Pixels', value: 'px' },
  { label: 'Percent', value: '%' },
];

const borderStyleOptions = [
  { label: 'None', value: 'none' },
  { label: 'Solid', value: 'solid' },
  { label: 'Dashed', value: 'dashed' },
  { label: 'Dotted', value: 'dotted' },
];

export const dynamicGridEditingConfigs = (componentDocument: any) => {
  const stickyGridPosition = componentDocument?.props?.stickyGrid?.position;
  const marketplaceOfferIds =
    componentDocument?.props?.options?.marketplaceOfferIds?.split(',') || [];
  const stickyGridProps: any[] = [];

  if (stickyGridPosition === 'sticky') {
    stickyGridProps.push(
      numberInputSpec('stickyGrid.margin', 'Margin Top when Sticky (in px)', {
        help: 'The margin is set to 96px by default to account for the header height.',
      })
    );
  }

  if (stickyGridPosition === 'fixed') {
    stickyGridProps.push(
      stringSelectSpec('stickyGrid.location', 'Position', [
        { label: 'Top', value: 'top' },
        { label: 'Bottom', value: 'bottom' },
      ]),
      numberInputSpec('stickyGrid.margin', 'Margin From Position (in px)', {
        help: 'The margin is set to 0px by default.',
      }),
      stringInputSpec('stickyGrid.linkedComponentId', 'Linked Component ID', {
        help: `The id of the component that will trigger this container's visibility. If left blank, the container will always be visible.`,
      })
    );
  }

  // centered in parent background color config
  const centeredInParentBackgroundConfig = () => {
    const centeredInParentHexColor =
      componentDocument?.props?.options?.centeredInParentBackgroundColor ===
      'HEXCOLOR'
        ? [stringInputSpec('options.centeredInParentHexColor', 'Hex Color')]
        : [];

    const inputsConfig = componentDocument?.props?.options?.centeredInParent
      ? [
          colorPicker(
            'options.centeredInParentBackgroundColor',
            'Centered In Parent BackgroundColor'
          ),
          ...centeredInParentHexColor,
        ]
      : [];
    return inputsConfig;
  };

  return [
    group('Options', [
      boolCheckboxSpec(
        'options.disableGridOutline',
        'Disable Grid Outline/Transparency?'
      ),
      boolCheckboxSpec('options.centeredInParent', 'Centered in Parent?'),
      ...centeredInParentBackgroundConfig(),
      stringTextAreaSpec(
        'options.marketplaceOfferIds',
        'Marketplace Offer IDs',
        {
          help: 'Enter the marketplace offer IDs for the product, separated by commas',
        }
      ),
      group('Sticky Settings', [
        stringSelectSpec('stickyGrid.position', 'Sticky Container Behavior', [
          { label: 'Default', value: 'relative' },
          { label: 'On Scroll', value: 'sticky' },
          { label: 'Fixed', value: 'fixed' },
        ]),
        ...stickyGridProps,
      ]),
      group('Modal Settings', [
        boolCheckboxSpec('modal.enabled', 'Output as a modal?'),
        stringInputSpec('modal.id', 'Modal ID'),
        boolCheckboxSpec('modal.showCloseButton', 'Show Close Button?'),
        boolCheckboxSpec('modal.shouldOpenOnMouseMove', 'Open on Mouse Move?'),
        numberInputSpec('modal.timeoutValue', 'Timeout Value', {
          help: 'Timeout value in seconds.',
        }),
        stringSelectSpec('modal.verticalAlignment', 'Vertical Alignment', [
          { label: 'Center', value: 'center' },
          { label: 'Top', value: 'flex-start' },
          { label: 'Bottom', value: 'flex-end' },
        ]),
        stringSelectSpec('modal.horizontalAlignment', 'Horizontal Alignment', [
          { label: 'Center', value: 'center' },
          { label: 'Left', value: 'flex-start' },
          { label: 'Right', value: 'flex-end' },
        ]),
        collapsable('modal.closeButtonProps', 'Close Button Props', [
          stringInputSpec('fontSize', 'Font Size'),
          colorPicker('color', 'Color'),
        ]),
        collapsable(
          'modal.borders',
          'Modal Borders',
          [
            stringInputSpec('borderColor', 'Border Color'),
            stringSelectSpec('borderStyle', 'Border Style', borderStyleOptions),
            boolCheckboxSpec('boxShadow', 'Apply Box Shadow?'),
            stringInputSpec('boxShadowColor', 'Box Shadow Color'),
            numberInputSpec('borderTopWidth', 'Border Top Width'),
            numberInputSpec('borderRightWidth', 'Border Right Width'),
            numberInputSpec('borderBottomWidth', 'Border Bottom Width'),
            numberInputSpec('borderLeftWidth', 'Border Left Width'),
            numberInputSpec('borderTopLeftRadius', 'Border Top Left Radius'),
            numberInputSpec('borderTopRightRadius', 'Border Top Right Radius'),
            numberInputSpec(
              'borderBottomRightRadius',
              'Border Bottom Right Radius'
            ),
            numberInputSpec(
              'borderBottomLeftRadius',
              'Border Bottom Left Radius'
            ),
            collapsable('tabletBorders', 'Tablet', [
              stringInputSpec('borderColor', 'Border Color'),
              stringSelectSpec(
                'borderStyle',
                'Border Style',
                borderStyleOptions
              ),
              boolCheckboxSpec('boxShadow', 'Apply Box Shadow?'),
              stringInputSpec('boxShadowColor', 'Box Shadow Color'),
              numberInputSpec('borderTopWidth', 'Border Top Width'),
              numberInputSpec('borderRightWidth', 'Border Right Width'),
              numberInputSpec('borderBottomWidth', 'Border Bottom Width'),
              numberInputSpec('borderLeftWidth', 'Border Left Width'),
              numberInputSpec('borderTopLeftRadius', 'Border Top Left Radius'),
              numberInputSpec(
                'borderTopRightRadius',
                'Border Top Right Radius'
              ),
              numberInputSpec(
                'borderBottomRightRadius',
                'Border Bottom Right Radius'
              ),
              numberInputSpec(
                'borderBottomLeftRadius',
                'Border Bottom Left Radius'
              ),
            ]),
            collapsable('mobileBorders', 'Mobile', [
              stringInputSpec('borderColor', 'Border Color'),
              stringSelectSpec(
                'borderStyle',
                'Border Style',
                borderStyleOptions
              ),
              boolCheckboxSpec('boxShadow', 'Apply Box Shadow?'),
              stringInputSpec('boxShadowColor', 'Box Shadow Color'),
              numberInputSpec('borderTopWidth', 'Border Top Width'),
              numberInputSpec('borderRightWidth', 'Border Right Width'),
              numberInputSpec('borderBottomWidth', 'Border Bottom Width'),
              numberInputSpec('borderLeftWidth', 'Border Left Width'),
              numberInputSpec('borderTopLeftRadius', 'Border Top Left Radius'),
              numberInputSpec(
                'borderTopRightRadius',
                'Border Top Right Radius'
              ),
              numberInputSpec(
                'borderBottomRightRadius',
                'Border Bottom Right Radius'
              ),
              numberInputSpec(
                'borderBottomLeftRadius',
                'Border Bottom Left Radius'
              ),
            ]),
          ],
          {
            icon: BorderIcon,
          }
        ),
        collapsable(
          'modal.spacing',
          'Modal Spacing',
          [
            numberInputSpec('paddingTop', 'Padding Top'),
            numberInputSpec('paddingRight', 'Padding Right'),
            numberInputSpec('paddingBottom', 'Padding Bottom'),
            numberInputSpec('paddingLeft', 'Padding Left'),
            stringSelectSpec(
              'paddingUnit',
              'Padding Unit',
              marginAndPaddingUnitOptions
            ),
            numberInputSpec('marginTop', 'Margin Top'),
            numberInputSpec('marginRight', 'Margin Right'),
            numberInputSpec('marginBottom', 'Margin Bottom'),
            numberInputSpec('marginLeft', 'Margin Left'),
            stringSelectSpec(
              'marginUnit',
              'Margin Unit',
              marginAndPaddingUnitOptions
            ),
            collapsable('tabletSpacing', 'Tablet', [
              numberInputSpec('paddingTop', 'Padding Top'),
              numberInputSpec('paddingRight', 'Padding Right'),
              numberInputSpec('paddingBottom', 'Padding Bottom'),
              numberInputSpec('paddingLeft', 'Padding Left'),
              numberInputSpec('marginTop', 'Margin Top'),
              numberInputSpec('marginRight', 'Margin Right'),
              numberInputSpec('marginBottom', 'Margin Bottom'),
              numberInputSpec('marginLeft', 'Margin Left'),
            ]),
            collapsable('mobileSpacing', 'Mobile', [
              numberInputSpec('paddingTop', 'Padding Top'),
              numberInputSpec('paddingRight', 'Padding Right'),
              numberInputSpec('paddingBottom', 'Padding Bottom'),
              numberInputSpec('paddingLeft', 'Padding Left'),
              numberInputSpec('marginTop', 'Margin Top'),
              numberInputSpec('marginRight', 'Margin Right'),
              numberInputSpec('marginBottom', 'Margin Bottom'),
              numberInputSpec('marginLeft', 'Margin Left'),
            ]),
          ],
          {
            icon: SpacingIcon,
          }
        ),
        collapsable(
          'modal.style',
          'Modal Style',
          [
            colorPicker('backgroundColor', 'Background Color'),
            stringInputSpec('backgroundColorHex', 'Hex Color Override'),
          ],
          {
            icon: StyleIcon,
          }
        ),
      ]),
    ]),

    (() => {
      // let currentKey: string | null = null;
      const generateElementConfig = () => {
        // get the current key path for the current item
        // const getKey = (key: string) => {
        //   currentKey = key;
        // };

        // removing the first element from the key path
        // const currentKeyPath = currentKey
        //   ? currentKey?.split('.')?.splice(1).join('.') || ''
        //   : 'props.elements';
        // const currentKeyPath = currentKey?.split('.')?.splice(1).join('.');

        // const styleArray = get(
        //   componentDocument,
        //   `${currentKeyPath}.${item}.layout`,
        //   []
        // );

        // const styleAsObject = Object.assign({}, ...styleArray);
        // const layout = styleAsObject?.layout || 'flex';

        // const gridColumnsInputConfigMainContainer = get(
        //   componentDocument,
        //   `props.elements.${item}.layout`
        // );

        // const gridColumnsInputConfigMainContainerStyle = Object.assign(
        //   {},
        //   ...(gridColumnsInputConfigMainContainer || [])
        // );

        // const mainContainerGridColumnsInputConfig =
        //   gridColumnsInputConfigMainContainerStyle?.layout === 'grid'
        //     ? [numberInputSpec('gridColumns', 'Grid Columns')]
        //     : [];

        // const gridColumnsInputConfig =
        //   !!currentKey && layout === 'grid'
        //     ? [numberInputSpec('gridColumns', 'Grid Columns')]
        //     : [];

        return [
          collapsable(
            'info',
            'Container Info',
            [stringInputSpec('id', 'Container ID')],
            {
              icon: InfoIcon,
            }
          ),
          collapsable(
            'borders',
            'Borders',
            [
              stringInputSpec('borderColor', 'Border Color'),
              stringSelectSpec(
                'borderStyle',
                'Border Style',
                borderStyleOptions
              ),
              boolCheckboxSpec('boxShadow', 'Apply Box Shadow?'),
              stringInputSpec('boxShadowColor', 'Box Shadow Color'),
              numberInputSpec('borderTopWidth', 'Border Top Width'),
              numberInputSpec('borderRightWidth', 'Border Right Width'),
              numberInputSpec('borderBottomWidth', 'Border Bottom Width'),
              numberInputSpec('borderLeftWidth', 'Border Left Width'),
              numberInputSpec('borderTopLeftRadius', 'Border Top Left Radius'),
              numberInputSpec(
                'borderTopRightRadius',
                'Border Top Right Radius'
              ),
              numberInputSpec(
                'borderBottomRightRadius',
                'Border Bottom Right Radius'
              ),
              numberInputSpec(
                'borderBottomLeftRadius',
                'Border Bottom Left Radius'
              ),
              collapsable('tabletBorders', 'Tablet', [
                stringInputSpec('borderColor', 'Border Color'),
                stringSelectSpec(
                  'borderStyle',
                  'Border Style',
                  borderStyleOptions
                ),
                boolCheckboxSpec('boxShadow', 'Apply Box Shadow?'),
                stringInputSpec('boxShadowColor', 'Box Shadow Color'),
                numberInputSpec('borderTopWidth', 'Border Top Width'),
                numberInputSpec('borderRightWidth', 'Border Right Width'),
                numberInputSpec('borderBottomWidth', 'Border Bottom Width'),
                numberInputSpec('borderLeftWidth', 'Border Left Width'),
                numberInputSpec(
                  'borderTopLeftRadius',
                  'Border Top Left Radius'
                ),
                numberInputSpec(
                  'borderTopRightRadius',
                  'Border Top Right Radius'
                ),
                numberInputSpec(
                  'borderBottomRightRadius',
                  'Border Bottom Right Radius'
                ),
                numberInputSpec(
                  'borderBottomLeftRadius',
                  'Border Bottom Left Radius'
                ),
              ]),
              collapsable('mobileBorders', 'Mobile', [
                stringInputSpec('borderColor', 'Border Color'),
                stringSelectSpec(
                  'borderStyle',
                  'Border Style',
                  borderStyleOptions
                ),
                boolCheckboxSpec('boxShadow', 'Apply Box Shadow?'),
                stringInputSpec('boxShadowColor', 'Box Shadow Color'),
                numberInputSpec('borderTopWidth', 'Border Top Width'),
                numberInputSpec('borderRightWidth', 'Border Right Width'),
                numberInputSpec('borderBottomWidth', 'Border Bottom Width'),
                numberInputSpec('borderLeftWidth', 'Border Left Width'),
                numberInputSpec(
                  'borderTopLeftRadius',
                  'Border Top Left Radius'
                ),
                numberInputSpec(
                  'borderTopRightRadius',
                  'Border Top Right Radius'
                ),
                numberInputSpec(
                  'borderBottomRightRadius',
                  'Border Bottom Right Radius'
                ),
                numberInputSpec(
                  'borderBottomLeftRadius',
                  'Border Bottom Left Radius'
                ),
              ]),
            ],
            {
              icon: BorderIcon,
            }
          ),
          collapsable(
            'dimensions',
            'Dimensions',
            [
              numberInputSpec('height', 'Height'),
              stringSelectSpec(
                'heightUnit',
                'Height Unit',
                heightAndWidthUnitOptions
              ),
              numberInputSpec('width', 'Width'),
              stringSelectSpec(
                'widthUnit',
                'Width Unit',
                heightAndWidthUnitOptions
              ),
              collapsable('tabletDimensions', 'Tablet', [
                numberInputSpec('height', 'Height'),
                stringSelectSpec(
                  'heightUnit',
                  'Height Unit',
                  heightAndWidthUnitOptions
                ),
                numberInputSpec('width', 'Width'),
                stringSelectSpec(
                  'widthUnit',
                  'Width Unit',
                  heightAndWidthUnitOptions
                ),
              ]),
              collapsable('mobileDimensions', 'Mobile', [
                numberInputSpec('height', 'Height'),
                stringSelectSpec(
                  'heightUnit',
                  'Height Unit',
                  heightAndWidthUnitOptions
                ),
                numberInputSpec('width', 'Width'),
                stringSelectSpec(
                  'widthUnit',
                  'Width Unit',
                  heightAndWidthUnitOptions
                ),
              ]),
            ],
            {
              icon: DimensionsIcon,
            }
          ),
          collapsable(
            'layout',
            'Layout',
            [
              stringSelectSpec('layout', 'Layout', [
                { label: 'Flex', value: 'flex' },
                { label: 'Grid', value: 'grid' },
              ]),
              numberInputSpec('gridColumns', 'Grid Columns'),
              stringSelectSpec('elementType', 'Component Type', [
                { label: 'Row', value: 'row' },
                { label: 'Row Reverse', value: 'row-reverse' },
                { label: 'Column', value: 'column' },
                { label: 'Column Reverse', value: 'column-reverse' },
              ]),
              stringSelectSpec('alignItems', 'Align Items', [
                { label: 'Start', value: 'flex-start' },
                { label: 'Center', value: 'center' },
                { label: 'End', value: 'flex-end' },
                { label: 'Stretch', value: 'stretch' },
                { label: 'Baseline', value: 'baseline' },
              ]),
              stringSelectSpec('justifyContent', 'Justify Content', [
                { label: 'Start', value: 'flex-start' },
                { label: 'Center', value: 'center' },
                { label: 'End', value: 'flex-end' },
                { label: 'Space-between', value: 'space-between' },
                { label: 'Space-around', value: 'space-around' },
                { label: 'Space-evenly', value: 'space-evenly' },
              ]),
              boolCheckboxSpec('isSticky', 'Apply Position Sticky?'),
              boolCheckboxSpec(
                'isFullWidthSticky',
                'Apply Full Width When Stuck?'
              ),
              numberInputSpec('stickyTop', 'Margin Top when Sticky (in px)', {
                help: 'For desktop the margin is set to 96px by default to account for the header height.',
              }),
              collapsable('tabletLayout', 'Tablet', [
                numberInputSpec('gridColumns', 'Grid Columns'),
                stringSelectSpec('elementType', 'Component Type on Tablet', [
                  { label: 'Row', value: 'row' },
                  { label: 'Row Reverse', value: 'row-reverse' },
                  { label: 'Column', value: 'column' },
                  { label: 'Column Reverse', value: 'column-reverse' },
                ]),
                stringSelectSpec('alignItems', 'Align Items', [
                  { label: '', value: null },
                  { label: 'Top', value: 'flex-start' },
                  { label: 'Center', value: 'center' },
                  { label: 'Bottom', value: 'flex-end' },
                  { label: 'Stretch', value: 'stretch' },
                  { label: 'Baseline', value: 'baseline' },
                ]),
                stringSelectSpec('justifyContent', 'Justify Content', [
                  { label: '', value: null },
                  { label: 'Left', value: 'flex-start' },
                  { label: 'Center', value: 'center' },
                  { label: 'Right', value: 'flex-end' },
                  { label: 'Space-between', value: 'space-between' },
                  { label: 'Space-around', value: 'space-around' },
                  { label: 'Space-evenly', value: 'space-evenly' },
                ]),
                boolCheckboxSpec('isSticky', 'Apply Position Sticky?'),
                boolCheckboxSpec(
                  'isFullWidthSticky',
                  'Apply Full Width When Stuck?'
                ),
                numberInputSpec('stickyTop', 'Margin Top when Sticky (in px)'),
              ]),
              collapsable('mobileLayout', 'Mobile', [
                numberInputSpec('gridColumns', 'Grid Columns'),
                stringSelectSpec('elementType', 'Component Type on Mobile', [
                  { label: 'Row', value: 'row' },
                  { label: 'Row Reverse', value: 'row-reverse' },
                  { label: 'Column', value: 'column' },
                  { label: 'Column Reverse', value: 'column-reverse' },
                ]),
                stringSelectSpec('alignItems', 'Align Items', [
                  { label: '', value: null },
                  { label: 'Top', value: 'flex-start' },
                  { label: 'Center', value: 'center' },
                  { label: 'Bottom', value: 'flex-end' },
                  { label: 'Stretch', value: 'stretch' },
                  { label: 'Baseline', value: 'baseline' },
                ]),
                stringSelectSpec('justifyContent', 'Justify Content', [
                  { label: '', value: null },
                  { label: 'Left', value: 'flex-start' },
                  { label: 'Center', value: 'center' },
                  { label: 'Right', value: 'flex-end' },
                  { label: 'Space-between', value: 'space-between' },
                  { label: 'Space-around', value: 'space-around' },
                  { label: 'Space-evenly', value: 'space-evenly' },
                ]),
                boolCheckboxSpec('isSticky', 'Apply Position Sticky?'),
                boolCheckboxSpec(
                  'isFullWidthSticky',
                  'Apply Full Width When Stuck?'
                ),
                numberInputSpec('stickyTop', 'Margin Top when Sticky (in px)'),
              ]),
            ],
            {
              icon: LayoutIcon,
            }
          ),
          collapsable(
            'spacing',
            'Spacing',
            [
              numberInputSpec('paddingTop', 'Padding Top'),
              numberInputSpec('paddingRight', 'Padding Right'),
              numberInputSpec('paddingBottom', 'Padding Bottom'),
              numberInputSpec('paddingLeft', 'Padding Left'),
              stringSelectSpec(
                'paddingUnit',
                'Padding Unit',
                marginAndPaddingUnitOptions
              ),
              numberInputSpec('marginTop', 'Margin Top'),
              numberInputSpec('marginRight', 'Margin Right'),
              numberInputSpec('marginBottom', 'Margin Bottom'),
              numberInputSpec('marginLeft', 'Margin Left'),
              stringSelectSpec(
                'marginUnit',
                'Margin Unit',
                marginAndPaddingUnitOptions
              ),
              collapsable('tabletSpacing', 'Tablet', [
                numberInputSpec('paddingTop', 'Padding Top'),
                numberInputSpec('paddingRight', 'Padding Right'),
                numberInputSpec('paddingBottom', 'Padding Bottom'),
                numberInputSpec('paddingLeft', 'Padding Left'),
                numberInputSpec('marginTop', 'Margin Top'),
                numberInputSpec('marginRight', 'Margin Right'),
                numberInputSpec('marginBottom', 'Margin Bottom'),
                numberInputSpec('marginLeft', 'Margin Left'),
              ]),
              collapsable('mobileSpacing', 'Mobile', [
                numberInputSpec('paddingTop', 'Padding Top'),
                numberInputSpec('paddingRight', 'Padding Right'),
                numberInputSpec('paddingBottom', 'Padding Bottom'),
                numberInputSpec('paddingLeft', 'Padding Left'),
                numberInputSpec('marginTop', 'Margin Top'),
                numberInputSpec('marginRight', 'Margin Right'),
                numberInputSpec('marginBottom', 'Margin Bottom'),
                numberInputSpec('marginLeft', 'Margin Left'),
              ]),
            ],
            {
              icon: SpacingIcon,
            }
          ),
          collapsable(
            'style',
            'Style',
            [
              stringInputSpec(
                'backgroundImageDesktop',
                'Background Image URL (Desktop)'
              ),
              colorPicker('backgroundColor', 'Background Color'),
              stringInputSpec('backgroundColorHex', 'Hex Color Override'),
              stringSelectSpec('backgroundSize', 'Background Size', [
                { label: 'Cover', value: 'cover' },
                { label: 'Contain', value: 'contain' },
                { label: 'Auto', value: 'auto' },
              ]),
              stringSelectSpec('backgroundPositionX', 'Background Position X', [
                { label: 'Left', value: 'left' },
                { label: 'Center', value: 'center' },
                { label: 'Right', value: 'right' },
              ]),
              numberInputSpec(
                'backgroundPositionXDistance',
                'Distance From Edge for X Position (in px)'
              ),
              stringSelectSpec('backgroundPositionY', 'Background Position Y', [
                { label: 'Top', value: 'top' },
                { label: 'Center', value: 'center' },
                { label: 'Bottom', value: 'bottom' },
              ]),
              numberInputSpec(
                'backgroundPositionYDistance',
                'Distance From Edge for Y Position (in px)'
              ),
              collapsable('tabletStyle', 'Tablet', [
                stringInputSpec(
                  'backgroundImageTablet',
                  'Background Image URL (Tablet)'
                ),
                stringSelectSpec(
                  'backgroundPositionX',
                  'Background Position X',
                  [
                    { label: 'Left', value: 'left' },
                    { label: 'Center', value: 'center' },
                    { label: 'Right', value: 'right' },
                  ]
                ),
                numberInputSpec(
                  'backgroundPositionXDistance',
                  'Distance From Edge for X Position (in px)'
                ),
                stringSelectSpec(
                  'backgroundPositionY',
                  'Background Position Y',
                  [
                    { label: 'Top', value: 'top' },
                    { label: 'Center', value: 'center' },
                    { label: 'Bottom', value: 'bottom' },
                  ]
                ),
                numberInputSpec(
                  'backgroundPositionYDistance',
                  'Distance From Edge for Y Position (in px)'
                ),
              ]),
              collapsable('mobileStyle', 'Mobile', [
                stringInputSpec(
                  'backgroundImageMobile',
                  'Background Image URL (Mobile)'
                ),
                stringSelectSpec(
                  'backgroundPositionX',
                  'Background Position X',
                  [
                    { label: 'Left', value: 'left' },
                    { label: 'Center', value: 'center' },
                    { label: 'Right', value: 'right' },
                  ]
                ),
                numberInputSpec(
                  'backgroundPositionXDistance',
                  'Distance From Edge for X Position (in px)'
                ),
                stringSelectSpec(
                  'backgroundPositionY',
                  'Background Position Y',
                  [
                    { label: 'Top', value: 'top' },
                    { label: 'Center', value: 'center' },
                    { label: 'Bottom', value: 'bottom' },
                  ]
                ),
                numberInputSpec(
                  'backgroundPositionYDistance',
                  'Distance From Edge for Y Position (in px)'
                ),
              ]),
            ],
            {
              icon: StyleIcon,
            }
          ),
          collapsable(
            'hide',
            'Hide',
            [
              boolCheckboxSpec('hideOnMobile', 'Hide on Mobile?'),
              boolCheckboxSpec('hideOnTablet', 'Hide on Tablet?'),
              boolCheckboxSpec('hideOnDesktop', 'Hide on Desktop?'),
            ],
            {
              icon: HideIcon,
            }
          ),
          collapsable(
            'floating',
            'Floating',
            [
              boolCheckboxSpec('enabled', 'Enabled?'),
              numberInputSpec('floatTop', 'Float Top'),
              numberInputSpec('floatBottom', 'Float Bottom'),
              numberInputSpec('floatLeft', 'Float Left'),
              numberInputSpec('floatRight', 'Float Right'),
              numberInputSpec('floatIndex', 'Float Index'),
              stringSelectSpec('floatUnit', 'Float Unit', floatingUnitOptions),
            ],
            {
              icon: FloatingIcon,
            }
          ),
          collapsable(
            'tracking',
            'Tracking',
            [
              boolCheckboxSpec('enabled', 'Enable Segment Tracking'),
              stringSelectSpec('marketplaceOfferId', 'Marketplace Offer ID', [
                { label: 'Select Marketplace Offer ID', value: '' },
                ...marketplaceOfferIds?.map((id: string) => ({
                  label: id,
                  value: id,
                })),
              ]),
              stringSelectSpec('eventType', 'Event Type', [
                { label: 'SKU Viewed', value: 'skuViewed' },
                { label: 'Viewed', value: 'viewed' },
              ]),
              stringInputSpec('entityName', 'Entity Name'),
              stringInputSpec('impressionSection', 'Impression Section'),
              stringInputSpec('impressionPosition', 'Impression Position'),
            ],
            {
              icon: AnalyticsIcon,
            }
          ),
          repeatable(
            'components',
            '',
            'component',
            () => [
              stringSelectSpec('componentType', 'Component Type', [
                { label: 'Select', value: '' },
                { label: 'Accordion', value: 'accordion' },
                {
                  label: 'Advertiser Disclosure',
                  value: 'advertiser-disclosure',
                },
                {
                  label: 'Auto Insurance Matches',
                  value: 'auto-insurance-matches',
                },
                { label: 'Bullet List', value: 'bulletList' },
                { label: 'Button', value: 'button' },
                {
                  label: 'Fixed First Column Table',
                  value: 'fixed-first-column-table',
                },
                { label: 'Image', value: 'image' },
                { label: 'Link', value: 'link' },
                { label: 'Lottie Animation', value: 'lottie-animation' },
                {
                  label: 'Press Releases Archive',
                  value: 'press-releases-archive',
                },
                { label: 'Star Rating', value: 'star-rating' },
                { label: 'Text', value: 'text' },
                { label: 'Third Party Embed', value: 'third-party-embed' },
                { label: 'Tooltip', value: 'tooltip' },
              ]),
            ],
            {
              icon: ComponentIcon,
            }
          ),
          repeatable('elements', '', 'Container', generateElementConfig, {
            icon: ContainerIcon,
            // getKey,
          }),
        ];
      };

      return repeatable('elements', '', 'Container', generateElementConfig, {
        icon: ContainerIcon,
      });
    })(),
  ];
};
